import { useState } from 'react';
import styled from '@emotion/styled';
import type { ExtractedAnswersAPI } from '@trinity/chat';
import { ExtractedAnswerCard } from './ExtractedAnswerCard';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BucketSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BucketHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  border-bottom: 1px solid #e0e0e0;
  padding: 0 0 4px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const BucketTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const Chevron = styled.span<{ collapsed: boolean }>`
  font-size: 14px;
  color: #666;
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.collapsed ? '0deg' : '180deg')});
  user-select: none;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: #999;
  font-size: 14px;
`;

type CollapsibleBucketProps = {
  title: string;
  items: ExtractedAnswersAPI.GetExtractedAnswers.ExtractedAnswerItem[];
};

function CollapsibleBucket({ title, items }: CollapsibleBucketProps): React.ReactElement {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <BucketSection>
      <BucketHeader type="button" onClick={() => setCollapsed((v) => !v)}>
        <BucketTitle>{title}</BucketTitle>
        <Chevron collapsed={collapsed}>▾</Chevron>
      </BucketHeader>
      {!collapsed && items.map((item) => <ExtractedAnswerCard key={item.id} item={item} />)}
    </BucketSection>
  );
}

type Props = {
  items: ExtractedAnswersAPI.GetExtractedAnswers.ExtractedAnswerItem[];
};

export function ExtractedAnswersList({ items }: Props): React.ReactElement {
  if (items.length === 0) {
    return <EmptyState>No extracted answers yet.</EmptyState>;
  }

  // Group by bucket
  const buckets = new Map<string, ExtractedAnswersAPI.GetExtractedAnswers.ExtractedAnswerItem[]>();
  const unbucketed: ExtractedAnswersAPI.GetExtractedAnswers.ExtractedAnswerItem[] = [];

  for (const item of items) {
    const bucket = item.question.answerConfig.bucket;
    if (bucket) {
      const list = buckets.get(bucket) ?? [];
      list.push(item);
      buckets.set(bucket, list);
    } else {
      unbucketed.push(item);
    }
  }

  return (
    <Container>
      {[...buckets.entries()].map(([bucket, bucketItems]) => (
        <CollapsibleBucket key={bucket} title={bucket} items={bucketItems} />
      ))}
      {unbucketed.length > 0 && (
        <CollapsibleBucket title={buckets.size > 0 ? 'Other' : ''} items={unbucketed} />
      )}
    </Container>
  );
}
